import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Badge, TICKET_STATUS, fmtDateTime } from "../lib/helpers";
import { useAuth } from "../context/AuthContext";
import { useFocusTrap } from "../hooks/useFocusTrap";
import AuthFileLink from "./AuthFileLink";

// Pastille d'étape ou de phase, comme dans la conversation (contour marine, jamais une couleur de statut).
const LineBadge = ({ kind, label }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold leading-none px-2 py-1 rounded-full border border-marine/25 text-marine bg-white whitespace-nowrap">
    <Icons.ListChecks size={12} aria-hidden="true" />{kind} · {label}
  </span>
);

const CLIENT_TYPES = ["Modification texte/image site", "Demande nouveau contenu", "Question livrable", "Demande urgente", "Récupération livrable expiré", "Autre"];

const cfg = (role) => {
  if (role === "admin") return { list: "/admin/tickets", read: (id) => `/admin/tickets/${id}/read`, reply: (id) => `/admin/tickets/${id}/reply`, status: (id) => `/admin/tickets/${id}/status`, canCreate: false, title: "À traiter" };
  if (role === "collaborator") return { list: "/collab/tickets", read: (id) => `/collab/tickets/${id}/read`, reply: (id) => `/collab/tickets/${id}/reply`, create: "/collab/tickets", canCreate: true, title: "Mes messages à l'agence", createLabel: "Nouveau message" };
  return { list: "/client/tickets", read: (id) => `/client/tickets/${id}/read`, reply: (id) => `/client/tickets/${id}/reply`, create: "/client/tickets", canCreate: true, title: "Demandes & Messages", createLabel: "Nouvelle demande" };
};

const lastMsg = (t) => (t.messages && t.messages.length ? t.messages[t.messages.length - 1] : null);

export const RequestsDrawer = () => {
  const { user } = useAuth();
  const c = cfg(user?.role);
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [creating, setCreating] = useState(false);
  const [todo, setTodo] = useState([]);  // agence : « À traiter » (01/10)
  const [form, setForm] = useState({ type: "", subject: "", description: "" });
  const [file, setFile] = useState(null);
  const ref = useRef();

  const isUnread = (t) => {
    const m = lastMsg(t);
    if (!m) return false;
    if (user?.role === "admin") return m.sender_role !== "admin" && !t.admin_read;
    return m.sender_role === "admin" && !(t.requester_read ?? t.client_read);
  };
  // Agence : la pastille compte les lignes « À traiter » ; entreprise et collaborateur : les réponses non lues.
  const unread = user?.role === "admin" ? todo.length : tickets.filter(isUnread).length;
  const nav = useNavigate();

  // Broadcast unread count so the mobile nav drawer can mirror this badge
  // without duplicating ticket-fetching logic.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("requests-unread-changed", { detail: { count: unread } }));
  }, [unread]);

  const load = async () => {
    try { const { data } = await api.get(c.list); setTickets(data); } catch { /* ignore */ }
    if (user?.role === "admin") { try { const { data } = await api.get("/admin/a-traiter"); setTodo(data.items || []); } catch { /* ignore */ } }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-line */ }, []);
  useEffect(() => {
    const onOpen = (e) => {
      setOpen(true);
      const tid = e.detail?.ticketId;
      if (tid) setTimeout(() => openTicket({ id: tid }), 300);
      // Pré-remplissage : une page peut ouvrir le volet sur une demande déjà amorcée (26/10, les
      // comptes à identifier). La personne relit et complète avant d'envoyer, rien ne part seul.
      const pre = e.detail?.prefill;
      if (pre) { setCreating(true); setForm({ type: pre.type || "", subject: pre.subject || "", description: pre.description || "" }); }
    };
    window.addEventListener("open-requests", onOpen);
    return () => window.removeEventListener("open-requests", onOpen);
    // eslint-disable-line
  }, []); // eslint-disable-line

  const openTicket = async (t) => {
    let full = tickets.find((x) => x.id === t.id) || t;
    setActive(full); setCreating(false);
    if (isUnread(full) || t._forceRead) { try { await api.post(c.read(t.id)); } catch { /* ignore */ } load(); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    const { data } = await api.post(c.reply(active.id), { text: reply });
    setActive({ ...active, messages: [...active.messages, data] }); setReply(""); load();
  };

  // « Voir le message » : la conversation du projet ou de la mission, à l'endroit du message ; une demande s'ouvre ici.
  const seeLine = (it) => {
    if (it.source === "ticket") return openTicket({ id: it.id });
    setOpen(false);
    nav(it.source === "project" ? `/admin/projets/${it.parent_id}?vue=conversation&message=${it.id}`
      : `/admin/missions/${it.parent_id}?vue=conversation&message=${it.id}`);
  };
  const handleLine = async (it) => {
    const url = it.source === "project" ? `/admin/projects/${it.parent_id}/comments/${it.id}/handled`
      : `/admin/missions/${it.parent_id}/messages/${it.id}/handled`;
    try { await api.post(url); toast.success("Marqué comme traité"); } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    load();
  };

  const setStatus = async (s) => { await api.put(c.status(active.id), { status: s }); toast.success("Statut mis à jour"); setActive({ ...active, status: s }); load(); };

  const submitCreate = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    if (user?.role === "client") fd.append("type", form.type || CLIENT_TYPES[0]);
    else fd.append("type", "Message");
    fd.append("subject", form.subject); fd.append("description", form.description);
    if (file) fd.append("file", file);
    // Pièce jointe refusée (type, taille) : le message du serveur, et la saisie reste (recette du 15/10, ANO-09).
    try { await api.post(c.create, fd); }
    catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible, réessayez."); return; }
    toast.success("Envoyé"); setCreating(false); setForm({ type: "", subject: "", description: "" }); setFile(null); load();
  };

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useFocusTrap(ref, open, () => setOpen(false));

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="requests-btn" aria-label={user?.role === "admin" ? "À traiter" : "Demandes"}
        className="relative hidden lg:flex w-9 h-9 rounded-full items-center justify-center text-white/85 hover:bg-white/10 hover:text-white transition-colors">
        <Icons.MessageSquare size={16} />
        {unread > 0 && <span data-testid="requests-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-ambre text-white text-[10px] font-semibold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-[65] bg-black/30" data-testid="requests-overlay">
          <div ref={ref} tabIndex={-1} data-testid="requests-drawer" className="absolute right-0 top-0 h-full w-full max-w-md bg-creme shadow-2xl flex flex-col animate-in slide-in-from-right duration-200 outline-none">
            <div className="flex items-center justify-between px-5 h-16 border-b border-marine/10 bg-white shrink-0">
              <div className="font-display text-lg text-marine">{active ? (active.subject || "Demande") : c.title}</div>
              <div className="flex items-center gap-2">
                {active && <button onClick={() => setActive(null)} data-testid="requests-back" className="text-xs text-marine/80 hover:text-marine flex items-center gap-1"><Icons.ChevronLeft size={16} className="text-marine" /> Retour</button>}
                <button onClick={() => setOpen(false)} data-testid="requests-close" aria-label="Fermer" title="Fermer"><Icons.X size={16} className="text-marine" /></button>
              </div>
            </div>

            {/* LISTE */}
            {!active && !creating && (
              <div className="flex-1 overflow-y-auto">
                {c.canCreate && (
                  <div className="p-4 border-b border-marine/10">
                    <button onClick={() => setCreating(true)} data-testid="requests-new-btn" className="w-full bg-marine text-white rounded-sm py-2.5 text-sm hover:bg-marine-hover transition-colors flex items-center justify-center gap-2"><Icons.Plus size={16} /> {c.createLabel}</button>
                  </div>
                )}
                {user?.role === "admin" && (
                  <div data-testid="a-traiter">
                    <p className="px-5 py-3 text-sm text-marine/80 border-b border-marine/10">Messages qui attendent une réponse écrite de Metara, et demandes ouvertes. Une ligne disparaît quand vous la marquez comme traitée.</p>
                    <div className="divide-y divide-marine/5">
                      {todo.map((it) => (
                        <div key={`${it.source}-${it.id}`} data-testid={`a-traiter-${it.id}`} className="px-5 py-4 grid grid-cols-1 gap-1.5">
                          <div className="text-xs text-bordeaux uppercase tracking-wider">
                            {it.source === "mission" || it.who_role === "collaborator" ? `Équipe · ${it.who}` : it.who}{it.kind_label ? ` · ${it.kind_label}` : ""}
                          </div>
                          <div className="font-medium text-marine flex items-center gap-2">{it.unread && <span className="w-2 h-2 rounded-full bg-rouge shrink-0" />}{it.title}</div>
                          {it.badge_label && <div><LineBadge kind={it.badge_kind} label={it.badge_label} /></div>}
                          <div className="text-sm text-marine/80 whitespace-pre-line break-words">« {it.excerpt} »</div>
                          <div className="flex flex-wrap items-center justify-between gap-2 mt-0.5">
                            <span className="text-xs text-marine/80">{fmtDateTime(it.created_at)}</span>
                            <span className="flex flex-wrap gap-2">
                              <button type="button" onClick={() => seeLine(it)} data-testid={`a-traiter-voir-${it.id}`} className="text-xs font-semibold text-white bg-marine rounded-sm px-2.5 py-1.5 hover:bg-marine-hover">{it.source === "ticket" ? "Ouvrir la demande" : "Voir le message"}</button>
                              {it.source !== "ticket" && <button type="button" onClick={() => handleLine(it)} data-testid={`a-traiter-traite-${it.id}`} className="text-xs font-semibold text-marine bg-white border border-marine/30 rounded-sm px-2.5 py-1.5 hover:bg-marine-50">Marquer comme traité</button>}
                            </span>
                          </div>
                        </div>
                      ))}
                      {todo.length === 0 && <p className="text-sm text-marine/80 text-center py-10">Rien à traiter pour l'instant.</p>}
                    </div>
                  </div>
                )}
                {user?.role !== "admin" && <div className="divide-y divide-marine/5">
                  {tickets.map((t) => (
                    <button key={t.id} onClick={() => openTicket(t)} data-testid={`request-${t.id}`} className="w-full text-left px-5 py-4 hover:bg-white transition-colors">
                      <div className="text-xs text-bordeaux uppercase tracking-wider mb-0.5">{(t.requester_role === "collaborator" ? "Équipe" : (t.client_name || t.requester_name || "Entreprise"))} · {t.type}</div>
                      <div className="font-medium text-marine flex items-center gap-2">{isUnread(t) && <span className="w-2 h-2 rounded-full bg-rouge shrink-0" />}{t.subject}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-marine/80">{fmtDateTime(t.created_at)} · {t.messages?.length || 0} msg</span>
                        <Badge status={t.status} map={TICKET_STATUS} />
                      </div>
                    </button>
                  ))}
                  {tickets.length === 0 && <p className="text-sm text-marine/80 text-center py-10">Aucune demande.</p>}
                </div>}
              </div>
            )}

            {/* CRÉATION */}
            {creating && (
              <form onSubmit={submitCreate} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="requests-create-form">
                {user?.role === "client" && (
                  <>
                    <label className="text-sm block">Type
                      <select value={form.type || CLIENT_TYPES[0]} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="request-type" className="inp mt-1">{CLIENT_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                    </label>
                  </>
                )}
                <label className="text-sm block">Objet<input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="request-subject" className="inp mt-1" /></label>
                <label className="text-sm block">Message<textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="request-desc" className="inp mt-1 resize-y" /></label>
                <label className="text-sm block">Pièce jointe (optionnel)<input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="request-file" className="text-sm mt-1 block" /></label>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
                  <button type="submit" data-testid="request-submit" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover">Envoyer</button>
                </div>
              </form>
            )}

            {/* FIL */}
            {active && (
              <>
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                  {user?.role === "admin" && (
                    <div className="flex gap-2 mb-2">{["ouvert", "en_cours", "resolu"].map((s) => <button key={s} onClick={() => setStatus(s)} data-testid={`request-status-${s}`} className={`px-3 py-1 rounded-sm text-xs border ${active.status === s ? "bg-marine text-white border-marine" : "border-marine/20 text-marine"}`}>{TICKET_STATUS[s].label}</button>)}</div>
                  )}
                  {active.attachment && <AuthFileLink path={active.attachment.path} className="text-xs text-bordeaux underline flex items-center gap-1"><Icons.Paperclip size={16} /> {active.attachment.name}</AuthFileLink>}
                  {(active.messages || []).map((m) => {
                    const mine = m.sender_role === user?.role;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : ""}`}>
                        <div className={`max-w-[80%] rounded-sm px-4 py-2.5 ${mine ? "bg-marine text-white" : "bg-sable border border-marine/30 text-marine"}`}>
                          <div className="text-xs opacity-70 mb-0.5">{m.sender_name} · {fmtDateTime(m.created_at)}</div>
                          <div className="text-sm whitespace-pre-wrap">{m.text}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {active.status !== "resolu" && (
                  <div className="p-4 border-t border-marine/10 bg-white flex gap-2 shrink-0">
                    <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} data-testid="request-reply" placeholder="Écrire un message..." className="flex-1 border border-marine/20 rounded-sm p-2.5 text-sm outline-none focus:ring-1 focus:ring-marine" />
                    <button onClick={sendReply} data-testid="request-reply-send" className="bg-marine text-white rounded-sm px-4 hover:bg-marine-hover"><Icons.Send size={16} /></button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
